/* eslint-disable max-lines */
import { Col, Form, notification, Row } from 'antd';
import { observer } from 'mobx-react-lite';
import moment, { Moment } from 'moment';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePickerField } from 'src/components/Form/DatePickerField';
import { NumberField } from 'src/components/Form/NumberField';
import { RadioGroupField } from 'src/components/Form/RadioGroupField';
import { SelectField } from 'src/components/Form/SelectField';
import { TextField } from 'src/components/Form/TextField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import { Modal } from 'src/components/Modal/Modal';
import {
    ApproveLoanApplicationRequest,
    CreateFinalOfferRequestInstallmentOptionEnum,
} from 'src/generated-api-client';
import { useLoadResources } from 'src/hooks/useLoadResources';
import { AgentLoanApplicationsStore } from 'src/stores/AgentLoanApplicationsStore/AgentLoanApplicationsStore';
import { InstallmentOptionResourceStore } from 'src/stores/Resources/InstallmentOptionResource';
import { PaymentFreequencyResourceStore } from 'src/stores/Resources/PaymentFreequencyResourceStore';
import { FormHelper } from 'src/utils/FormHelper';
import { addModalRenderer, ModalRendererProps } from 'src/utils/ModalRenderer';
import styled from 'styled-components';

type InitialApprovePartnerApplicationDialogProps = ModalRendererProps<{
    applicationId: string;
}>;

const InitialApprovePartnerApplicationDialogFn = observer(
    ({
        applicationId,
        onSuccess,
        ...props
    }: InitialApprovePartnerApplicationDialogProps) => {
        const { t } = useTranslation();
        const fields = FormHelper.getFieldDataItems();
        const { isLoadingResources } = useLoadResources([
            PaymentFreequencyResourceStore,
            InstallmentOptionResourceStore,
        ]);
        const [form] = Form.useForm();

        useEffect(() => {
            return () => {
                store.approveLoader.reset();
            };
        }, []);

        const installmentOption: InitialApprovePartnerApplicationFormValues['finalOffer']['installmentOption'] =
            Form.useWatch(
                formNames.getPath(['finalOffer', 'installmentOption']),
                form,
            );

        const financingAmount: InitialApprovePartnerApplicationFormValues['finalOffer']['financingAmount'] =
            Form.useWatch(
                formNames.getPath(['finalOffer', 'financingAmount']),
                form,
            );

        const onFinish = async (
            values: InitialApprovePartnerApplicationFormValues,
        ) => {
            await store.approve(applicationId, values);
            if (!store.approveLoader.hasError) {
                notification.success({
                    message: t(
                        'App.InitialApprovePartnerApplicationDialog.Success',
                    ),
                });
                onSuccess?.();
            } else {
                return Promise.reject();
            }
        };

        return (
            <StyledModal
                title={t('App.InitialApprovePartnerApplicationDialog.Title')}
                okText={t('App.InitialApprovePartnerApplicationDialog.Confirm')}
                isLoading={store.approveLoader.isLoading || isLoadingResources}
                form={form}
                initialValues={{
                    finalOffer: {
                        installmentOption:
                            CreateFinalOfferRequestInstallmentOptionEnum.Percentage,
                    },
                }}
                formProps={{ fields }}
                onFinish={onFinish}
                {...props}
            >
                <Row gutter={16}>
                    <Col xs={24}>
                        <TextField
                            name={formNames.getPath(['finalOffer', 'name'])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.Name',
                            )}
                            rules={[ValidationRules.required]}
                        />
                    </Col>
                    <Col xs={24}>
                        <TextField
                            name={formNames.getPath([
                                'finalOffer',
                                'agreementNumber',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.AgreementNumber',
                            )}
                            rules={[ValidationRules.required]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'financingAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.FinancingAmount',
                            )}
                            rules={[
                                ValidationRules.required,
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(1000000000),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <DatePickerField
                            name={formNames.getPath([
                                'finalOffer',
                                'firstPaymentDate',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.FirstPaymentDate',
                            )}
                            rules={[ValidationRules.required]}
                            pickerProps={{
                                disabledDate: (date: Moment) => {
                                    return date.isSameOrBefore(moment());
                                },
                            }}
                        />
                    </Col>
                    <Col xs={12}>
                        <DatePickerField
                            name={formNames.getPath([
                                'finalOffer',
                                'finalSettlementDate',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.FinalSettlementDate',
                            )}
                            rules={[ValidationRules.required]}
                            pickerProps={{
                                disabledDate: (date: Moment) => {
                                    return date.isSameOrBefore(moment());
                                },
                            }}
                        />
                    </Col>
                    <Col xs={12}>
                        <SelectField
                            name={formNames.getPath([
                                'finalOffer',
                                'paymentFrequency',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.PaymentFrequency',
                            )}
                            rules={[ValidationRules.required]}
                            options={PaymentFreequencyResourceStore.options}
                        />
                    </Col>
                    <Col xs={24}>
                        <RadioGroupField
                            name={formNames.getPath([
                                'finalOffer',
                                'installmentOption',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.InstallmentOption',
                            )}
                            rules={[ValidationRules.required]}
                            options={InstallmentOptionResourceStore.options}
                        />
                    </Col>
                    <Col xs={12}>
                        {installmentOption ===
                            CreateFinalOfferRequestInstallmentOptionEnum.FixedValue && (
                            <NumberField
                                name={formNames.getPath([
                                    'finalOffer',
                                    'installmentAmount',
                                ])}
                                label={t(
                                    'App.InitialApprovePartnerApplicationDialog.InstallmentAmount',
                                )}
                                rules={[
                                    ValidationRules.required,
                                    ValidationRules.greaterThanZero,
                                    ValidationRules.numberMaxValue(10000000),
                                ]}
                            />
                        )}
                        {installmentOption ===
                            CreateFinalOfferRequestInstallmentOptionEnum.Percentage && (
                            <NumberField
                                name={formNames.getPath([
                                    'finalOffer',
                                    'deductionRatePct',
                                ])}
                                label={t(
                                    'App.InitialApprovePartnerApplicationDialog.DeductionRatePct',
                                )}
                                rules={[
                                    ValidationRules.required,
                                    ValidationRules.greaterThanZero,
                                    ValidationRules.numberMaxValue(99),
                                ]}
                            />
                        )}
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'holdbackRatePct',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.HoldbackRatePct',
                            )}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(99),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'minInstallmentAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.MinInstallmentAmount',
                            )}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(10000000),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'maxInstallmentAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.MaxInstallmentAmount',
                            )}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(10000000),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath(['finalOffer', 'term'])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.Term',
                            )}
                            rules={[
                                ValidationRules.required,
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(120),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'murabahaRatePct',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.MurabahaRatePct',
                            )}
                            rules={[
                                ValidationRules.required,
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(300),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'totalMurabahaAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.TotalMurabahaAmount',
                            )}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(1000000000),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'totalRepayableAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.TotalRepayableAmount',
                            )}
                            tooltip={t(
                                'App.InitialApprovePartnerApplicationDialog.TotalRepayableAmount.Tooltip',
                            )}
                            dependencies={[
                                formNames.getPath([
                                    'finalOffer',
                                    'financingAmount',
                                ]),
                            ]}
                            rules={[
                                ValidationRules.required,
                                ValidationRules.numberMinValue(
                                    financingAmount || 0,
                                ),
                                ValidationRules.numberMaxValue(1000000000),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'disbursementAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.DisbursementAmount',
                            )}
                            rules={[
                                ValidationRules.required,
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(
                                    financingAmount || 0,
                                ),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'adminFeeRatePct',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.AdminFeeRatePct',
                            )}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(99),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'adminFeeAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.AdminFeeAmount',
                            )}
                            dependencies={[
                                formNames.getPath([
                                    'finalOffer',
                                    'financingAmount',
                                ]),
                            ]}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(
                                    financingAmount ? financingAmount - 1 : 1,
                                ),
                            ]}
                        />
                    </Col>
                    <Col xs={12}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'adminFeeWithVatAmount',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.AdminFeeWithVatAmount',
                            )}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(1000000),
                            ]}
                        />
                    </Col>
                    <Col xs={24}>
                        <TextField
                            name={formNames.getPath(['finalOffer', 'iban'])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.Iban',
                            )}
                            inputProps={{
                                placeholder: 'SA0380000000608010167519',
                            }}
                            rules={[ValidationRules.required]}
                        />
                    </Col>
                    <Col xs={24}>
                        <NumberField
                            name={formNames.getPath([
                                'finalOffer',
                                'collectionCommissionPct',
                            ])}
                            label={t(
                                'App.InitialApprovePartnerApplicationDialog.CollectionCommissionPct',
                            )}
                            rules={[
                                ValidationRules.greaterThanZero,
                                ValidationRules.numberMaxValue(99),
                            ]}
                        />
                    </Col>
                </Row>
            </StyledModal>
        );
    },
);

const StyledModal = styled(Modal)`
    max-height: 90vh;
    overflow: auto;
    padding: 0;
    width: 600px !important;

    .ant-modal-content {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
    }
`;

type InitialApprovePartnerApplicationFormValues = ApproveLoanApplicationRequest;
const formNames =
    FormHelper.createNameProvider<InitialApprovePartnerApplicationFormValues>();
const store = AgentLoanApplicationsStore;

export const InitialApprovePartnerApplicationDialog = addModalRenderer(
    InitialApprovePartnerApplicationDialogFn,
);
